import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Ban, BadgeCheck, CheckCircle2, Paperclip, Pencil, Plus, Receipt, Repeat, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import {
  useAttachBillMutation,
  useBillActionMutation,
  useBillsQuery,
  useOpenDocument,
  useRunRecurringMutation,
  useUpsertBillMutation,
} from '@/client/api/business/spend';
import { BILL_STATUS_BADGE, type Bill, type BillStatus } from '@/client/types/business/spend';
import { AccountingPageShell } from './components/AccountingPageShell';
import { Field, filterClass, inputClass, textareaClass, today, useSpendLookups } from './components/spend-ui';
import { DeleteConfirmDialog, EmptyState, RowMenu, StatusBadge } from '../HumanResource/components/hr-ui';
import { money, totals } from '../HumanResource/components/comp-format';
import { formatDay } from '../HumanResource/components/leave-format';

const TABS: (BillStatus | 'open' | 'all')[] = ['open', 'draft', 'approved', 'paid', 'void', 'all'];
const TAB_LABEL: Record<string, string> = { open: 'To pay', draft: 'Draft', approved: 'Approved', paid: 'Paid', void: 'Void', all: 'All' };

type Form = { vendorId: string; number: string; issueDate: string; dueDate: string; amount: string; currency: string; categoryId: string; departmentId: string; description: string; notes: string };

export default function BillsPage() {
  const [params, setParams] = useSearchParams();
  const vendorFilter = params.get('vendor') ?? '';
  const billsQ = useBillsQuery();
  const lookups = useSpendLookups();
  const upsert = useUpsertBillMutation();
  const act = useBillActionMutation();
  const attach = useAttachBillMutation();
  const runRecurring = useRunRecurringMutation();
  const openDoc = useOpenDocument('business');
  const canManage = useGate(PERMISSIONS.accounting.manage).allowed;
  const [tab, setTab] = useState<(typeof TABS)[number]>('open');
  const [editing, setEditing] = useState<Bill | null | 'new'>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [pending, setPending] = useState<{ bill: Bill; action: 'approve' | 'void' } | null>(null);
  const [paying, setPaying] = useState<Bill | null>(null);
  const [payForm, setPayForm] = useState({ paidOn: today(), paymentRef: '' });
  const fileRef = useRef<HTMLInputElement>(null);
  const [attachFor, setAttachFor] = useState<Bill | null>(null);

  const bills = billsQ.data ?? [];
  const scoped = bills.filter((b) => !vendorFilter || b.vendorId === vendorFilter);
  const shown = scoped.filter((b) => (tab === 'all' ? true : tab === 'open' ? b.status === 'draft' || b.status === 'approved' : b.status === tab));
  const open = scoped.filter((b) => b.status === 'draft' || b.status === 'approved');
  const monthStart = today().slice(0, 7);

  const stats = useMemo(
    () => [
      { key: 'owed', label: 'Owed', value: totals(open) },
      { key: 'overdue', label: 'Overdue', value: totals(open.filter((b) => b.overdue)) },
      { key: 'drafts', label: 'Drafts to review', value: scoped.filter((b) => b.status === 'draft').length },
      { key: 'paid', label: 'Paid this month', value: totals(scoped.filter((b) => b.status === 'paid' && (b.paidOn ?? '').startsWith(monthStart))) },
    ],
    [open, scoped, monthStart],
  );

  useEffect(() => {
    if (editing === null) return setForm(null);
    const b = editing === 'new' ? null : editing;
    const vendor = lookups.vendors.find((v) => v.id === (b?.vendorId ?? vendorFilter));
    setForm({
      vendorId: b?.vendorId ?? vendorFilter,
      number: b?.number ?? '',
      issueDate: b?.issueDate ?? today(),
      dueDate: b?.dueDate ?? '',
      amount: b ? String(b.amount) : '',
      currency: b?.currency ?? vendor?.defaultCurrency ?? 'GHS',
      categoryId: b?.categoryId ?? '',
      departmentId: b?.departmentId ?? '',
      description: b?.description ?? '',
      notes: b?.notes ?? '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    upsert.mutate(
      {
        id: editing && editing !== 'new' ? editing.id : undefined,
        vendorId: form.vendorId,
        number: form.number,
        issueDate: form.issueDate,
        dueDate: form.dueDate || null,
        amount: Number(form.amount),
        currency: form.currency.trim().toUpperCase(),
        categoryId: form.categoryId || null,
        departmentId: form.departmentId || null,
        description: form.description,
        notes: form.notes,
      },
      {
        onSuccess: () => {
          toast.success(editing === 'new' ? 'Bill added as a draft' : 'Bill updated');
          setEditing(null);
        },
        onError: (err) => toast.error((err as Error).message || 'Could not save the bill'),
      },
    );
  };

  const done = (msg: string) => ({ onSuccess: () => toast.success(msg), onError: (e: unknown) => toast.error((e as Error).message) });

  return (
    <AccountingPageShell
      mock={false}
      title="Bills"
      description="What we owe vendors. Recurring charges add their bills as drafts every morning. Draft → approved → paid."
      crumbs={[{ label: 'Accounting', href: '/admin/accounting' }, { label: 'Payables' }, { label: 'Bills' }]}
      icon={Receipt}
      actions={
        <div className="flex gap-2">
          <PermissionButton
            code={PERMISSIONS.accounting.manage}
            variant="outline"
            className="gap-2"
            disabled={runRecurring.isPending}
            onClick={() =>
              runRecurring.mutate(undefined, {
                onSuccess: (r) => toast.success(r.billsCreated ? `${r.billsCreated} draft bill(s) added` : 'No recurring charges are due'),
                onError: (e) => toast.error((e as Error).message),
              })
            }
          >
            <Repeat className="h-4 w-4" /> Bill recurring charges now
          </PermissionButton>
          <PermissionButton code={PERMISSIONS.accounting.manage} className="gap-2" onClick={() => setEditing('new')}>
            <Plus className="h-4 w-4" /> Add bill
          </PermissionButton>
        </div>
      }
      stats={stats}
    >
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="inline-flex flex-wrap rounded-lg border border-gray-200 bg-white p-1">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${tab === t ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              {TAB_LABEL[t]}
            </button>
          ))}
        </div>
        <select
          value={vendorFilter}
          onChange={(e) => setParams(e.target.value ? { vendor: e.target.value } : {})}
          className={filterClass}
        >
          <option value="">All vendors</option>
          {lookups.vendors.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </select>
      </div>

      {billsQ.isError ? (
        <EmptyState label={`Could not load bills: ${(billsQ.error as Error)?.message ?? 'unknown error'}`} />
      ) : billsQ.isLoading ? (
        <EmptyState label="Loading bills…" />
      ) : shown.length === 0 ? (
        <EmptyState label={tab === 'open' ? 'Nothing to pay.' : 'No bills here.'} />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">Vendor</th>
                <th className="px-4 py-3 font-medium">What</th>
                <th className="px-4 py-3 font-medium">Issued</th>
                <th className="px-4 py-3 font-medium">Due</th>
                <th className="px-4 py-3 font-medium">Amount</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {shown.map((b) => (
                <tr key={b.id} className={canManage && b.status === 'draft' ? 'cursor-pointer hover:bg-gray-50' : ''} onClick={() => canManage && b.status === 'draft' && setEditing(b)}>
                  <td className="px-4 py-3 font-medium text-gray-900">{lookups.vendorName(b.vendorId)}</td>
                  <td className="px-4 py-3">
                    <p className="text-gray-800">{b.description || b.number || '—'}</p>
                    <p className="text-xs text-gray-400">
                      {[b.number && `#${b.number}`, b.categoryId && lookups.categoryName(b.categoryId), b.recurringChargeId && 'recurring']
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{formatDay(b.issueDate)}</td>
                  <td className={`px-4 py-3 ${b.overdue ? 'font-medium text-red-600' : 'text-gray-600'}`}>
                    {b.dueDate ? formatDay(b.dueDate) : '—'}
                    {b.overdue && <span className="ml-1 text-xs">overdue</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-900">{money(b.amount, b.currency)}</td>
                  <td className="px-4 py-3">
                    <StatusBadge label={b.status} className={`capitalize ${BILL_STATUS_BADGE[b.status]}`} />
                    {b.paidOn && <p className="text-[11px] text-gray-400">{formatDay(b.paidOn)}{b.paymentRef && ` · ${b.paymentRef}`}</p>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <RowMenu
                      label="Bill actions"
                      items={[
                        { label: 'Edit', icon: Pencil, hidden: !canManage || b.status !== 'draft', onSelect: () => setEditing(b) },
                        { label: 'Approve', icon: CheckCircle2, hidden: !canManage || b.status !== 'draft', onSelect: () => setPending({ bill: b, action: 'approve' }) },
                        {
                          label: 'Mark paid',
                          icon: BadgeCheck,
                          hidden: !canManage || b.status !== 'approved',
                          onSelect: () => {
                            setPayForm({ paidOn: today(), paymentRef: '' });
                            setPaying(b);
                          },
                        },
                        { label: 'Open attachment', icon: Paperclip, hidden: !b.hasAttachment, onSelect: () => openDoc(`/bills/${b.id}/attachment`).catch((e) => toast.error((e as Error).message)) },
                        {
                          label: b.hasAttachment ? 'Replace attachment' : 'Attach invoice',
                          icon: Upload,
                          hidden: !canManage,
                          onSelect: () => {
                            setAttachFor(b);
                            fileRef.current?.click();
                          },
                        },
                        { label: 'Void', icon: Ban, danger: true, hidden: !canManage || (b.status !== 'draft' && b.status !== 'approved'), onSelect: () => setPending({ bill: b, action: 'void' }) },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="application/pdf,image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file && attachFor) attach.mutate({ id: attachFor.id, file }, done('Attached'));
        }}
      />

      <Dialog open={!!form} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Add bill' : 'Edit draft bill'}</DialogTitle>
          </DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Vendor">
                  <select required value={form.vendorId} onChange={(e) => setForm({ ...form, vendorId: e.target.value })} className={inputClass}>
                    <option value="">Select a vendor</option>
                    {lookups.vendors
                      .filter((v) => v.status === 'active' || v.id === form.vendorId)
                      .map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name}
                        </option>
                      ))}
                  </select>
                </Field>
                <Field label="Invoice number">
                  <input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} className={inputClass} />
                </Field>
                <Field label="Issued">
                  <input type="date" value={form.issueDate} onChange={(e) => setForm({ ...form, issueDate: e.target.value })} className={inputClass} />
                </Field>
                <Field label="Due">
                  <input type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} className={inputClass} />
                </Field>
                <Field label="Amount">
                  <input required type="number" min={0} step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputClass} />
                </Field>
                <Field label="Currency">
                  <input required maxLength={3} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className={inputClass} />
                </Field>
                <Field label="Category">
                  <select value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })} className={inputClass}>
                    <option value="">None</option>
                    {lookups.categories.filter((c) => c.status === 'active').map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Department">
                  <select value={form.departmentId} onChange={(e) => setForm({ ...form, departmentId: e.target.value })} className={inputClass}>
                    <option value="">Company-wide</option>
                    {lookups.departments.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <Field label="What it is for">
                <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputClass} />
              </Field>
              <Field label="Notes">
                <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={textareaClass} />
              </Field>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={upsert.isPending}>
                  {upsert.isPending ? 'Saving…' : 'Save draft'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!paying} onOpenChange={(o) => !o && setPaying(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Mark paid</DialogTitle>
          </DialogHeader>
          {paying && (
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                act.mutate({ id: paying.id, action: 'pay', ...payForm }, done('Marked paid'));
                setPaying(null);
              }}
            >
              <p className="text-sm text-gray-600">
                {lookups.vendorName(paying.vendorId)} · {money(paying.amount, paying.currency)}. Record this once the money has gone out.
              </p>
              <Field label="Paid on">
                <input type="date" required value={payForm.paidOn} onChange={(e) => setPayForm({ ...payForm, paidOn: e.target.value })} className={inputClass} />
              </Field>
              <Field label="Payment reference (bank / MoMo)">
                <input value={payForm.paymentRef} onChange={(e) => setPayForm({ ...payForm, paymentRef: e.target.value })} className={inputClass} />
              </Field>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button type="button" variant="outline" onClick={() => setPaying(null)}>
                  Cancel
                </Button>
                <Button type="submit">Mark paid</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={!!pending}
        onOpenChange={(o) => !o && setPending(null)}
        title={pending?.action === 'approve' ? 'Approve this bill?' : 'Void this bill?'}
        description={
          pending
            ? `${lookups.vendorName(pending.bill.vendorId)} · ${money(pending.bill.amount, pending.bill.currency)}. ${
                pending.action === 'approve' ? 'It can then be paid; it can no longer be edited.' : 'It stays in the history as void and no longer counts as owed.'
              }`
            : ''
        }
        actionLabel={pending?.action === 'approve' ? 'Approve' : 'Void'}
        danger={pending?.action === 'void'}
        onConfirm={() => {
          if (pending) act.mutate({ id: pending.bill.id, action: pending.action }, done(pending.action === 'approve' ? 'Approved' : 'Voided'));
          setPending(null);
        }}
      />
    </AccountingPageShell>
  );
}
